import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BiCheck } from 'react-icons/bi';
import { IoArrowBackSharp } from 'react-icons/io5';

import { CALENDAR_KINDS, type CalendarFilters, type CalendarKind } from '@/hooks/useCalendarFilter';
import { MENU_TAG_NAME } from '@/store/tags/tags.dto';
import { useEventTagsQuery } from '@/store/tags/tags.store';

import styles from './CalendarFilterPopover.module.scss';

export type PopoverViewOption<T extends string> = { value: T; label: string };

type CalendarFilterPopoverProps<T extends string> = {
  anchorEl: HTMLElement | null;
  view: T;
  viewOptions: readonly PopoverViewOption<T>[];
  onChangeView: (view: T) => void;
  filters: CalendarFilters;
  onUpdateFilters: (filters: Partial<CalendarFilters>) => void;
  withViews?: boolean;
  onClose: () => void;
};

const ALL_KINDS = CALENDAR_KINDS.map((kind) => kind.id);
const MIN_HEIGHT = 180;
const POPOVER_WIDTH = 240;

export const CalendarFilterPopover = <T extends string>({
  anchorEl,
  view,
  viewOptions,
  onChangeView,
  filters,
  onUpdateFilters,
  withViews = true,
  onClose,
}: CalendarFilterPopoverProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(
    null,
  );
  const [showFilters, setShowFilters] = useState(false);
  const [lastAnchor, setLastAnchor] = useState<HTMLElement | null>(anchorEl);
  const { data: vesselTags = [] } = useEventTagsQuery(!!anchorEl);

  if (lastAnchor !== anchorEl) {
    setLastAnchor(anchorEl);
    setShowFilters(false);
  }

  useEffect(() => {
    if (!anchorEl) return;
    const calculate = () => {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: Math.max(
          8,
          Math.min(rect.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - 8),
        ),
        maxHeight: Math.max(MIN_HEIGHT, window.innerHeight - rect.bottom - 16),
      });
    };
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, [anchorEl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorEl, onClose]);

  if (!anchorEl || !position) return null;

  const selected = filters.kinds ?? ALL_KINDS;

  const toggleKind = (kind: CalendarKind) => {
    const next = selected.includes(kind)
      ? selected.filter((item) => item !== kind)
      : [...selected, kind];
    onUpdateFilters({ kinds: next.length === ALL_KINDS.length ? undefined : next });
  };

  // "With meals" already stands for the Menu tag.
  const filterTags = vesselTags.filter((tag) => tag.name !== MENU_TAG_NAME);
  const selectedTagIds = filters.tagIds ?? [];

  const toggleTag = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];
    onUpdateFilters({ tagIds: next.length ? next : undefined });
  };

  return createPortal(
    <div
      ref={ref}
      className={styles.popover}
      role="dialog"
      aria-label="Filter the plan"
      style={{
        top: position.top,
        left: position.left,
        width: POPOVER_WIDTH,
        maxHeight: position.maxHeight,
      }}
    >
      <div className={styles.filterMenu}>
        {showFilters || !withViews ? (
          <>
            {withViews && (
              <button
                type="button"
                className={styles.backOption}
                onClick={() => setShowFilters(false)}
              >
                <IoArrowBackSharp aria-hidden="true" /> Back
              </button>
            )}

            <span className={styles.filterTitle}>Show</span>
            <ul className={styles.filterList}>
              {CALENDAR_KINDS.map((kind) => {
                const checked = selected.includes(kind.id);
                return (
                  <li key={kind.id}>
                    <button
                      type="button"
                      className={styles.filterOption}
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => toggleKind(kind.id)}
                    >
                      <span
                        className={`${styles.filterBox} ${checked ? styles.filterBoxChecked : ''}`}
                      />
                      <span
                        className={styles.filterDot}
                        aria-hidden="true"
                        style={{ background: kind.color }}
                      />
                      {kind.label}
                    </button>
                  </li>
                );
              })}
            </ul>

            <span className={styles.filterTitle}>Show Only</span>
            <ul className={styles.filterList}>
              <li>
                <button
                  type="button"
                  className={styles.filterOption}
                  role="checkbox"
                  aria-checked={!!filters.withGuests}
                  onClick={() => onUpdateFilters({ withGuests: !filters.withGuests })}
                >
                  <span
                    className={`${styles.filterBox} ${
                      filters.withGuests ? styles.filterBoxChecked : ''
                    }`}
                  />
                  With guests
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={styles.filterOption}
                  role="checkbox"
                  aria-checked={!!filters.withMeals}
                  onClick={() => onUpdateFilters({ withMeals: !filters.withMeals })}
                >
                  <span
                    className={`${styles.filterBox} ${
                      filters.withMeals ? styles.filterBoxChecked : ''
                    }`}
                  />
                  With meals
                </button>
              </li>
            </ul>

            {filterTags.length > 0 && (
              <>
                <span className={styles.filterTitle}>Tags</span>
                <ul className={styles.filterList}>
                  {filterTags.map((tag) => {
                    const checked = selectedTagIds.includes(tag.id);
                    return (
                      <li key={tag.id}>
                        <button
                          type="button"
                          className={styles.filterOption}
                          role="checkbox"
                          aria-checked={checked}
                          onClick={() => toggleTag(tag.id)}
                        >
                          <span
                            className={`${styles.filterBox} ${checked ? styles.filterBoxChecked : ''}`}
                          />
                          {tag.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        ) : (
          <>
            <ul className={styles.filterList}>
              {viewOptions.map((option) => {
                const active = option.value === view;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      className={`${styles.menuOption} ${active ? styles.menuOptionOn : ''}`}
                      onClick={() => {
                        onChangeView(option.value);
                        onClose();
                      }}
                    >
                      {option.label}
                      {active && <BiCheck aria-hidden="true" />}
                    </button>
                  </li>
                );
              })}
            </ul>
            <ul className={styles.filterList}>
              <li>
                <button
                  type="button"
                  className={styles.menuOption}
                  onClick={() => setShowFilters(true)}
                >
                  Filter
                </button>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CalendarFilterPopover;
